import { useState, CSSProperties, useEffect } from "react";
import dayjs from "dayjs";
import Select from "react-select";

import { MAIN_THEME } from "styles/colors";

import { selectComponentStyles } from "utils/selectStyles";
import { dateFilterTypes } from "utils/filters";
import {
	DateFilterOptionValue,
	DateRangeFilter,
	DateFilterType,
} from "interfaces/filters";
import { DateTimePicker } from "components/molecules";

interface Props {
	onApply: (params: DateRangeFilter) => void;
	onReset: () => void;
	defaultDateRange: DateRangeFilter;
	dateRange: DateRangeFilter;
	format?: string;
	hasTypes?: boolean;
}

interface SelectOption {
	value: DateFilterType;
	label: string;
}

const DateRangePicker = (props: Props): JSX.Element => {
	const { onApply, onReset, defaultDateRange, dateRange, format, hasTypes } =
		props;

	const [dateFrom, setDateFrom] = useState(dateRange.dateFrom);
	const [dateTo, setDateTo] = useState(dateRange.dateTo);
	const [dateFromType, setDateFromType] = useState(dateFilterTypes[1]);
	const [dateToType, setDateToType] = useState(dateFilterTypes[1]);
	const [selectedDateOption, setSelectedDateOption] = useState<any>();
	const [options, setOptions] = useState<any>([]);
	const [applyFilter, setApplyFilter] = useState(false);

	useEffect(() => {
		const numberOfMonths = dayjs().get("month");

		const months = [];
		const years = [
			{
				label: `${dayjs().year()} (Current)`,
				value: {
					dateFrom: dayjs().startOf("year").toISOString(),
					dateTo: dayjs().endOf("year").toISOString(),
				},
			},
			{
				label: `${dayjs().subtract(1, "year").year()}`,
				value: {
					dateTo: dayjs().subtract(1, "year").endOf("year").toISOString(),
					dateFrom: dayjs().subtract(1, "year").startOf("year").toISOString(),
				},
			},
		];

		for (let index = 0; index <= numberOfMonths; index++) {
			months.push({
				label: `${dayjs().month(index).format("MMM")} ${dayjs().year()} ${
					index === numberOfMonths ? "(Current)" : ""
				}`,
				value: {
					dateFrom: dayjs().month(index).startOf("month").toISOString(),
					dateTo: dayjs().month(index).endOf("month").toISOString(),
				},
			});
		}

		const optionsArray = [
			{
				label: "Custom",
				value: DateFilterOptionValue.CUSTOM,
			},
			{
				label: "Year",
				options: years,
			},
		];

		if (months.length) {
			optionsArray.push({
				label: "Months",
				options: months.reverse(),
			});
		}

		const selectedMonthOption = months.find(
			(month) =>
				month.value.dateFrom === dateRange.dateFrom &&
				month.value.dateTo === dateRange.dateTo
		);

		const selectedYearOption = years.find(
			(year) =>
				year.value.dateFrom === dateRange.dateFrom &&
				year.value.dateTo === dateRange.dateTo
		);

		setOptions(optionsArray);

		if (selectedMonthOption) {
			setSelectedDateOption(selectedMonthOption);
		} else if (selectedYearOption) {
			setSelectedDateOption(selectedYearOption);
		}
	}, [dateRange]);

	useEffect(() => {
		if (applyFilter) {
			onApply({
				dateTo,
				dateFrom,
				dateFromType: dateFromType.value,
				dateToType: dateToType.value,
			});
			setApplyFilter(false);
		}
	}, [applyFilter]);

	const handleApply = () => {
		setApplyFilter(true);
	};

	const handleChangeDateFrom = (newDate: any) => {
		setDateFrom(
			newDate ? dayjs(newDate).toISOString() : defaultDateRange.dateFrom
		);
	};

	const handleChangeDateTo = (newDate: any) => {
		setDateTo(newDate ? dayjs(newDate).toISOString() : defaultDateRange.dateTo);
	};

	const handleReset = () => {
		setDateFrom(defaultDateRange.dateFrom);
		setDateTo(defaultDateRange.dateTo);

		onReset();
	};

	const changeDateFromType = (value: SelectOption | null) => {
		if (value) {
			setDateFromType(value);
			setDateToType(value);
		}
	};

	const handleChangeDateFilter = (option: any) => {
		setSelectedDateOption(option);

		if (option.value !== DateFilterOptionValue.CUSTOM) {
			handleChangeDateFrom(option.value.dateFrom);
			handleChangeDateTo(option.value.dateTo);
			setApplyFilter(true);
		}
	};

		const renderSelect = () => (
			<Select
				value={selectedDateOption}
				onChange={handleChangeDateFilter}
				styles={{
					...selectComponentStyles,
					container: (containerStyles: CSSProperties) => ({
						...containerStyles,
						width: "100%",
					}),
					placeholder: (pleceholderStyles: CSSProperties) => ({
						...pleceholderStyles,
						color: "#ffffff",
					}),
				}}
				options={options}
			/>
		);


	const renderApplyButton = () => (
		<button
			type="submit"
			onClick={handleApply}
			disabled={!selectedDateOption}
			className="flex h-10 justify-center items-center rounded-tr-[109px] rounded-br-[109px] border border-border bg-primary text-background font-bold text-lg cursor-pointer transition-all duration-200 hover:border-primary hover:bg-primary/90"
		>
			Apply
		</button>
	);

	const renderResetButton = () => (
		<button
			onClick={handleReset}
			className="flex h-10 justify-center items-center rounded-tl-[109px] rounded-bl-[109px] border border-border bg-secondary/20 text-foreground font-bold text-lg cursor-pointer transition-all duration-200 hover:border-primary hover:bg-primary/10"
		>
			Reset
		</button>
	);
		const renderTypes = () =>
			hasTypes && selectedDateOption?.value === DateFilterOptionValue.CUSTOM ? (
				<div className="grid gap-px mt-[25px] mb-[25px] rounded-[55px]">
					<Select
						value={dateFromType}
						onChange={changeDateFromType}
						styles={{
							...selectComponentStyles,
							container: (containerStyles: CSSProperties) => ({
								...containerStyles,
								width: "100%",
							}),
							placeholder: (pleceholderStyles: CSSProperties) => ({
								...pleceholderStyles,
								color: "#ffffff",
							}),
						}}
						options={dateFilterTypes}
					/>
				</div>
			) : null;


	const renderDateRangePicker = () => {
		if (selectedDateOption?.value === DateFilterOptionValue.CUSTOM) {
			return (
				<>
					<p className="font-medium text-foreground text-base mb-[15px]">Between:</p>
					<div className="grid grid-cols-2 gap-px w-full">
							<DateTimePicker
								onChange={handleChangeDateFrom}
								dateTime={dateFrom}
								borderedLeft
								format={format}
								hideCalendarIcon
								hideClearIcon
							/>
							<DateTimePicker
								onChange={handleChangeDateTo}
								dateTime={dateTo}
								borderedRight
								format={format}
								hideCalendarIcon
								hideClearIcon
							/>
					</div>
					<div className="grid grid-cols-2 gap-px mt-[25px]">
						{renderResetButton()}
						{renderApplyButton()}
					</div>
				</>
			);
		}
	};

	return (
		<div className="bg-secondary/10 border border-border shadow-[0_12px_30px_rgba(0,217,255,0.15)] backdrop-blur-[20px] rounded-[15px] p-[20px_30px] min-w-[320px] relative z-[5]">
			{renderSelect()}
			{renderTypes()}
			{renderDateRangePicker()}
		</div>
	);
};

export default DateRangePicker;
